import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import { resolveSentryConfig } from './config'

let initialized = false
const attachedApps = new WeakSet<App>()
const capturedErrors = new WeakSet<object>()

export function initializeSentry(): boolean {
  if (initialized) return true
  if (typeof __ANKER_SENTRY_CONFIG__ === 'undefined') return false
  const config = resolveSentryConfig(
    __ANKER_SENTRY_CONFIG__,
    typeof location === 'undefined' ? '' : location.hostname,
    import.meta.env.DEV
  )
  if (!config) {
    console.warn('[Sentry] Web monitoring disabled: missing or invalid environment DSN')
    return false
  }
  try {
    Sentry.init({
      ...config,
      integrations: (defaults) => defaults.filter((integration) => integration.name !== 'Vue'),
      initialScope: { tags: { app: 'web', platform: 'browser' } },
      sampleRate: 1,
      tracesSampleRate: 0,
      tracePropagationTargets: [],
      enhanceFetchErrorMessages: false,
      maxBreadcrumbs: 0,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: false,
        httpBodies: [],
        urlQueryParams: false,
      },
      beforeSend: (event) => {
        delete event.request
        delete event.user
        delete event.breadcrumbs
        return event
      },
    })
    initialized = true
    return true
  } catch {
    console.warn('[Sentry] Web monitoring initialization failed')
    return false
  }
}

export function attachVueSentry(app: App): void {
  if (!initialized || attachedApps.has(app)) return
  try {
    Sentry.addIntegration(Sentry.vueIntegration({ app, attachProps: false }))
    attachedApps.add(app)
  } catch {
    console.warn('[Sentry] Vue error integration unavailable')
  }
}

export function reportHttpError(
  error: unknown,
  request: { status?: number; method?: string; url?: string }
): void {
  const status = request.status
  if (!initialized || !status || status < 500 || status >= 600) return
  if (typeof error !== 'object' || error === null || capturedErrors.has(error)) return
  capturedErrors.add(error)
  try {
    const path = new URL(request.url || '/', 'https://local.invalid').pathname
    Sentry.captureException(new Error(`HTTP ${status}: ${path}`), {
      tags: { http_status: String(status) },
      extra: { method: request.method, path },
    })
  } catch {
    // Monitoring must not change the application's response or rejection.
  }
}

// Capture module/startup failures before importing the rest of the application.
initializeSentry()
